import { Link } from '@tanstack/react-router';
import { Box, SquarePen } from 'lucide-react';
import { cn } from '../lib/cn';
import type { UsageInfo } from '../lib/ws';
import { ContextMeter } from './ContextMeter';
import { MobileMenuButton } from './MobileMenuButton';
import { ModelSwitcher } from './ModelSwitcher';

type Props = {
  /** Chat title; omitted on the new-chat screen. */
  title?: string;
  artifactCount?: number;
  onOpenArtifacts?: () => void;
  artifactsOpen?: boolean;
  usage?: UsageInfo | null;
};

/**
 * App bar for chat screens, laid out like native chat apps: menu on
 * the left, chat title + model switcher centered, quick actions on the
 * right. Translucent so content scrolls softly underneath.
 */
export function ChatHeader({
  title,
  artifactCount = 0,
  onOpenArtifacts,
  artifactsOpen,
  usage,
}: Props) {
  return (
    <header className="mc-safe-top mc-safe-x mc-chrome relative z-20 grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-1 bg-background/85 px-2 py-1.5 backdrop-blur-xl md:px-4 md:py-2">
      <div className="flex min-w-0 items-center justify-start">
        <MobileMenuButton />
      </div>
      <div className="flex min-w-0 max-w-[min(60vw,420px)] justify-center">
        <ModelSwitcher subtitle={title} />
      </div>
      <div className="flex min-w-0 items-center justify-end gap-0.5">
        {usage && <ContextMeter usage={usage} />}
        {artifactCount > 0 && onOpenArtifacts && (
          <button
            type="button"
            onClick={onOpenArtifacts}
            className={cn(
              'mc-press relative inline-flex h-11 w-11 items-center justify-center rounded-full',
              artifactsOpen ? 'bg-bubble' : 'active:bg-subtle md:hover:bg-subtle',
            )}
            title={artifactsOpen ? 'Закрыть артефакты' : 'Открыть артефакты'}
            aria-label={`Артефакты: ${artifactCount}`}
            aria-pressed={!!artifactsOpen}
          >
            <Box size={19} strokeWidth={1.8} />
            <span className="absolute top-1.5 right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
              {artifactCount}
            </span>
          </button>
        )}
        {title !== undefined && (
          <Link
            to="/new"
            className="mc-press inline-flex h-11 w-11 items-center justify-center rounded-full text-foreground active:bg-subtle md:hover:bg-subtle"
            title="Новый чат"
            aria-label="Новый чат"
          >
            <SquarePen size={19} strokeWidth={1.8} />
          </Link>
        )}
      </div>
    </header>
  );
}
